/**
 * event-findings — what the events console has FOUND, in the shared card
 * grammar (./findings-kit).
 *
 * "Attention needed" was a leaderboard: ten rows sorted ascending by fill, no
 * owner, no action, no threshold — a reader had to decide for themselves
 * whether 61% was a problem. These findings answer that instead, and each one
 * names who it belongs to.
 *
 * Every number is derived from the SAME FillOccurrence rows the console already
 * holds (registrations ÷ capacity per occurrence, op-backed) — no new op, and
 * nothing here is a rate the screen doesn't already show somewhere.
 *
 * Two thresholds carry the judgement, and both are stated in the method line:
 *   • under-run   = fill < 40% of capacity
 *   • strong      = fill >= 80% (the console's own "perfect execution" bar)
 * Occurrences with NO capacity set are excluded from every fill figure on the
 * page; that exclusion is a finding of its own rather than a silent drop.
 */
import { share, sev, capAt, type FindingVerb, type FindingCardModel } from "./findings-kit";
import type { FillOccurrence } from "./reports-fill";

import { t } from "@v0y4ge/i18n-runtime";
export const UNDER_RUN = 40;
export const STRONG = 80;

export interface EventFinding extends FindingCardModel {
  /** The occurrences this finding is about — the card filter's target. */
  occurrences: FillOccurrence[];
  verb: FindingVerb | null;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
const seats = (rows: FillOccurrence[]) => rows.reduce((s, o) => s + Math.max(0, o.spaces - o.booked), 0);
/** The location carrying the most of a finding — the owner it routes to. */
function worstLocation(rows: FillOccurrence[]): { name: string; n: number } | null {
  const m = new Map<string, number>();
  rows.forEach((o) => m.set(o.storeName, (m.get(o.storeName) || 0) + 1));
  const best = [...m.entries()].sort((a, b) => b[1] - a[1])[0];
  return best ? { name: best[0], n: best[1] } : null;
}
function topRows(rows: FillOccurrence[], n = 3): { k: string; v: string }[] {
  return [...rows].sort((a, b) => a.fill - b.fill).slice(0, n)
    .map((o) => ({ k: `${o.name} · ${o.displayDate}`, v: `${o.booked}/${o.spaces} · ${o.fill}%` }));
}

/**
 * @param rows      occurrences in the analysis window, before any card scope
 * @param mode      which half of the console the reader is on
 * @param windowLbl the analysis window, for the method line
 */
export function eventFindings(rows: FillOccurrence[], mode: "past" | "future", windowLbl: string): EventFinding[] {
  const out: EventFinding[] = [];
  const rankable = rows.filter((o) => o.spaces > 0);
  const noCapacity = rows.filter((o) => o.spaces <= 0);
  const n = rankable.length;
  const small = n > 0 && n < 8 ? `On ${plural(n, "session")} — too few to be conclusive.` : null;
  const cap = (s: ReturnType<typeof sev>) => capAt(s, n > 0 && n < 8);
  const future = mode === "future";
  const method = `Fill = registrations ÷ capacity per occurrence, ${windowLbl.toLowerCase()}. Under-run < ${UNDER_RUN}%, strong ≥ ${STRONG}%. n=${n} ${n === 1 ? "session" : "sessions"} with a capacity set.`;

  /* 1 · Under-running sessions ───────────────────────────────────────── */
  const under = rankable.filter((o) => o.fill < UNDER_RUN);
  const underShare = share(under.length, n);
  const loc = worstLocation(under);
  out.push({
    id: "under", index: "1",
    value: String(under.length), unit: `of ${plural(n, "session")}`,
    headline: future ? "will under-run" : "under-ran",
    severity: cap(sev(underShare >= 0.25, underShare >= 0.1, under.length > 0)), note: small,
    category: "schedule",
    read: !under.length
      ? `Nothing in this window is below ${UNDER_RUN}% of its capacity. Whatever else needs attention, it is not empty rooms.`
      : future
      ? `${plural(under.length, "session")} scheduled in this window is filling below ${UNDER_RUN}% of capacity, and ${future ? "has not run yet" : "did not fill"} — still time to promote, merge or release the room.`
      : `${plural(under.length, "session")} finished below ${UNDER_RUN}% of capacity. Low fill is not automatically failure — a small clinic can be the right size — but ${seats(under)} seats went unused.`,
    impact: under.length
      ? `${seats(under)} unused seats${loc ? `; ${loc.name} carries ${loc.n} of them` : ""}.`
      : "None.",
    action: under.length
      ? (future
        ? "Promote, merge, or hand the room back before the date — whichever is cheapest per session."
        : "Review capacity against real demand before the next run; a smaller room is a legitimate fix.")
      : "Nothing to route.",
    owner: loc ? `Venue manager · ${loc.name}.` : "Nobody.",
    method,
    rows: topRows(under),
    occurrences: under,
    verb: under.length
      ? { id: "fill-review", label: future ? `Assign ${under.length} to a fill review` : "Assign a capacity review", kind: "handoff", done: "Assigned to the venue manager" }
      : null,
  });

  /* 2 · Nobody registered ────────────────────────────────────────────── */
  const empty = rankable.filter((o) => o.booked === 0);
  const emptyShare = share(empty.length, n);
  const eLoc = worstLocation(empty);
  out.push({
    id: "empty", index: "2",
    value: String(empty.length), unit: empty.length === 1 ? "session" : "sessions",
    headline: "have no registrations at all",
    severity: cap(sev(emptyShare >= 0.15, emptyShare >= 0.05, empty.length > 0)), note: small,
    category: "outreach",
    read: !empty.length
      ? "Every session in this window has at least one registration. Whatever is thin, the invitation reached somebody."
      : `Not the same finding as low fill: ${empty.length === 1 ? "this one has" : "these have"} nobody at all, which usually means the invitation never landed rather than that demand is soft.`,
    impact: empty.length
      ? `${seats(empty)} seats held for sessions nobody has booked${eLoc ? `; ${eLoc.n} at ${eLoc.name}` : ""}.`
      : "None.",
    action: empty.length
      ? "Check the session is published and reachable, then send the invitation before anything else."
      : "Nothing to route.",
    owner: t("reports.eventFindings.emptySessionsOwner", "Communications, with the event owner."),
    method: `Occurrences with zero registrations against a capacity greater than zero, ${windowLbl.toLowerCase()}.`,
    rows: topRows(empty),
    occurrences: empty,
    verb: empty.length
      ? { id: "invite-check", label: t("reports.eventFindings.routeToComms", "Route {count} to comms", { count: empty.length }), kind: "handoff", done: t("reports.eventFindings.routedToComms", "Routed to communications") }
      : null,
  });

  /* 3 · Capacity left on the table ───────────────────────────────────── */
  const unused = seats(rankable);
  const totalSpaces = rankable.reduce((s, o) => s + o.spaces, 0);
  const unusedShare = share(unused, totalSpaces);
  const avgCap = n ? Math.round(totalSpaces / n) : 0;
  out.push({
    id: "capacity", index: "3",
    value: unused.toLocaleString(), unit: avgCap ? `\u2248 ${Math.round(unused / avgCap)} sessions' worth` : "across the window",
    headline: "seats unused",
    severity: cap(sev(unusedShare >= 0.5, unusedShare >= 0.3, unused > 0)), note: small,
    category: "schedule",
    read: !unused
      ? "Every seat in this window was taken. Capacity is not the constraint here."
      : `${Math.round(unusedShare * 100)}% of the capacity offered in this window went unused${avgCap ? `, about ${Math.round(unused / avgCap)} average-sized sessions' worth` : ""}. That is staff time and rooms, whether or not anyone reads it as a shortfall.`,
    impact: unused
      ? `${unused.toLocaleString()} of ${totalSpaces.toLocaleString()} offered seats; ${plural(n - under.length, "session")} at or above ${UNDER_RUN}%.`
      : "None.",
    action: unused
      ? "Right-size the standing capacity before adding sessions — the same demand spread thinner reads as decline."
      : "Nothing to route.",
    owner: "Scheduling, with the venue managers.",
    method: `Sum of (capacity − registrations) over occurrences with a capacity set, ${windowLbl.toLowerCase()}.`,
    rows: [
      { k: "Offered seats", v: totalSpaces.toLocaleString() },
      { k: "Taken", v: (totalSpaces - unused).toLocaleString() },
      { k: `Sessions ≥ ${STRONG}%`, v: String(rankable.filter((o) => o.fill >= STRONG).length) },
    ],
    occurrences: rankable.filter((o) => o.spaces - o.booked > 0),
    verb: unused ? { id: "capacity-review", label: t("reports.eventFindings.label", "Assign a capacity review"), kind: "handoff", done: "Assigned to scheduling" } : null,
  });

  /* 4 · What is working ──────────────────────────────────────────────
   * The counterweight. A findings band that can only report problems teaches
   * the reader to discount it. */
  const strong = rankable.filter((o) => o.fill >= STRONG);
  const sLoc = worstLocation(strong);
  out.push({
    id: "strong", index: "4",
    value: String(strong.length), unit: `of ${plural(n, "session")}`,
    headline: `filled to ${STRONG}% or better`,
    severity: strong.length ? "clear" : "low", note: null,
    category: "schedule",
    read: strong.length
      ? `These are the sessions to copy, not just to celebrate: same catalogue, same channels, and they fill.${sLoc ? ` ${sLoc.name} holds ${sLoc.n} of them.` : ""}`
      : `Nothing in this window reached ${STRONG}%. Before treating that as a demand problem, check whether the capacities are simply set higher than the room ever needed.`,
    impact: strong.length
      ? `${strong.reduce((s, o) => s + o.booked, 0).toLocaleString()} attendees came through ${plural(strong.length, "session")}.`
      : "No high-fill session to learn from in this window.",
    action: strong.length
      ? "Take the format and the promotion of these to the thin ones before changing anything else."
      : "Compare the capacities set here against the ones that filled last term.",
    owner: strong.length ? "Event owners, as a worked example." : "Scheduling.",
    method: `Occurrences at or above ${STRONG}% fill, ${windowLbl.toLowerCase()}.`,
    rows: [...strong].sort((a, b) => b.fill - a.fill).slice(0, 3)
      .map((o) => ({ k: `${o.name} · ${o.displayDate}`, v: `${o.booked}/${o.spaces} · ${o.fill}%` })),
    occurrences: strong,
    verb: null,
  });

  /* 5 · Excluded rows ─────────────────────────────────────────────────
   * Only when there are any: an excluded-rows card with nothing in it is noise. */
  if (noCapacity.length) {
    out.push({
      id: "nocap", index: "5",
      value: String(noCapacity.length), unit: "occurrences excluded",
      headline: "have no capacity set",
      severity: share(noCapacity.length, rows.length) >= 0.1 ? "medium" : "low", note: null,
      category: "data",
      read: "Fill is a ratio, so an occurrence with no capacity cannot have one. These are absent from every fill figure on this page — not counted as zero, and not counted as full.",
      impact: `Every fill percentage here is computed on ${n} of ${rows.length} occurrences in the window.`,
      action: "Set a capacity on these occurrences, or mark them uncapped deliberately so the exclusion is a decision rather than a gap.",
      owner: "Event owners.",
      method: "Counted while reading the occurrence rows; an occurrence needs a capacity greater than zero to be rankable.",
      rows: [...noCapacity].slice(0, 3).map((o) => ({ k: `${o.name} · ${o.displayDate}`, v: `${o.booked} booked` })),
      occurrences: noCapacity,
      verb: null,
    });
  }
  return out;
}
